import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import Header from '../components/Layout/Header';
import AuthModal from '../components/Layout/AuthModal';
import { getValidUserSession } from '../utils/auth';
import { safeNextPath } from '../lib/authRedirect';
import usePageMeta from '../lib/usePageMeta';

/**
 * /login and /signup. Opens the site's sign-in form and, once the visitor is
 * signed in, returns them to `?next=` (an on-site path) or their dashboard.
 *
 * Closing the form never redirects: the page stays, with buttons to reopen
 * it, so there is no blank screen and no redirect loop.
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tab = location.pathname === '/signup' ? 'signup' : 'login';
  const next = safeNextPath(new URLSearchParams(location.search).get('next'));
  const [alreadySignedIn] = useState(() => Boolean(getValidUserSession()));
  const [open, setOpen] = useState(!alreadySignedIn);
  const [openTab, setOpenTab] = useState(tab);
  usePageMeta(tab === 'signup' ? 'Create an account' : 'Sign in', undefined, { noindex: true });

  useEffect(() => {
    if (alreadySignedIn) navigate(next, { replace: true });
  }, [alreadySignedIn, next, navigate]);

  const reopen = (which) => { setOpenTab(which); setOpen(true); };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <LogIn className="w-8 h-8 text-purple-600" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Sign in to continue</h1>
          <p className="text-gray-500 mb-8">Sign in or create a free account to keep going.</p>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => reopen('login')}
              className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors"
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => reopen('signup')}
              className="w-full h-12 border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Create a free account
            </button>
          </div>
        </div>
      </main>
      <AuthModal
        open={open}
        defaultTab={openTab}
        onClose={() => setOpen(false)}
        onAuthSuccess={() => navigate(next, { replace: true })}
      />
    </div>
  );
};

export default LoginPage;
